import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true
  );

  useEffect(() => {
    function handleResize() {
      setIsDesktop(window.innerWidth >= 768);
    }
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isDesktop;
}

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isDesktop = useIsDesktop();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 20);
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen && !isDesktop) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen, isDesktop]);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'shadow-md' : ''}`}
      style={{ backgroundColor: 'rgb(50, 45, 40)', borderBottom: '2px solid rgb(200, 160, 80)' }}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-lg font-heading font-bold whitespace-nowrap" style={{color:'#fff8eb'}}>pandabuy<span style={{color:'#dca040'}}>.blog</span></span>
          </Link>

          <div className="nav-desktop items-center gap-3">
            <Link
              to="/"
              className="px-3 py-2 rounded-full text-sm font-medium hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
              style={{color:'#f0e8d8'}}
            >
              Home
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-full text-sm font-medium hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
                style={{color:'#f0e8d8'}}
              >
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-72 bg-background-50 rounded-xl border border-background-200 shadow-xl py-2 max-h-[70vh] overflow-y-auto">
                  <p className="text-xs font-medium text-foreground-400 uppercase tracking-wider px-4 py-2">Shop Categories</p>
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className="w-9 h-9 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      <span className="whitespace-nowrap">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/blog/pandabuy-shopping-guide-2026"
              className="px-3 py-2 rounded-full text-sm font-medium hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
              style={{color:'#f0e8d8'}}
            >
              Shopping Guide
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-full border border-primary-400 text-sm hover:bg-primary-500 hover:text-white transition-all whitespace-nowrap cursor-pointer"
              style={{color:'#dca040'}}
            >
              Pandabuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full bg-primary-500 text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              style={{color:'#ffffff'}}
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="nav-mobile-btn w-10 h-10 items-center justify-center cursor-pointer"
            style={{color:'#f0e8d8'}}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileMenuOpen && !isDesktop && (
        <div className="fixed inset-0 top-16 bg-background-50/98 backdrop-blur-md z-40 overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-3">
            <div className="flex gap-2">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-background-100 text-sm font-medium text-foreground-800 hover:bg-background-200 transition-colors"
              >
                <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
                Home
              </Link>
              <Link
                to="/blog/pandabuy-shopping-guide-2026"
                onClick={() => setMobileMenuOpen(false)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-background-100 text-sm font-medium text-foreground-800 hover:bg-background-200 transition-colors"
              >
                <i className="ri-book-open-line w-4 h-4 flex items-center justify-center"></i>
                Shopping Guide
              </Link>
            </div>

            <div className="border-t border-background-200 pt-3">
              <p className="text-xs font-medium text-foreground-400 uppercase tracking-wider px-1 mb-2">Categories</p>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-background-100 text-sm text-foreground-700 hover:bg-background-200 hover:text-foreground-900 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                      <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                    </div>
                    <span className="text-xs leading-tight">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200 pt-3 flex flex-col gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-primary-500 text-primary-600 text-sm font-medium hover:bg-primary-500 hover:text-background-50 transition-all"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pandabuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all"
                onClick={() => setMobileMenuOpen(false)}
              >
                Buy It Now
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}